import { useEffect, useState, type ReactNode } from 'react';
import { api } from '@libero/core/services/api';
import {
  MapPinned,
  Truck,
  ShieldCheck,
  ShieldAlert,
  Loader2,
  FlaskConical,
  Webhook,
  PackageSearch,
  BarChart3,
  RefreshCw,
  CheckCircle2,
  Wand2,
  ArrowRight,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Workflow,
} from 'lucide-react';

const API_BASE = '/programmes/adresspruefung';

interface ValidationMessage {
  property: string;
  state: string;
  message: string;
  relevant: boolean;
}

interface TestResult {
  codeable: boolean;
  problems: string[];
  addressType: 'normal' | 'packstation' | 'filiale';
  messages: ValidationMessage[];
}

const input =
  'w-full px-3 py-2 rounded-lg border border-[#DAD9D9] text-sm focus:outline-none focus:border-[#1A1A1A] bg-white';
const label = 'block text-xs font-semibold text-[#1A1A1A]/70 mb-1';

export default function AdrespruefungApp() {
  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Header />
      <div className="max-w-5xl mx-auto px-6 py-4 space-y-4">
        <CheckPanel />
        <ReviewQueue />
        <AppliedCorrections />
        <ReconcileSection />
        <DashboardSection />
        <WorkflowDiagram />
        <TestCard />
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="border-b border-[#DAD9D9] bg-white">
      <div className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#E9FF57] flex items-center justify-center">
          <MapPinned className="w-5 h-5 text-[#1A1A1A]" strokeWidth={2.2} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[#1A1A1A] tracking-tight">Adressprüfung</h1>
          <p className="text-xs text-[#1A1A1A]/60 flex items-center gap-1">
            <Truck className="w-3.5 h-3.5" /> Versand · DHL-Leitcodierung
          </p>
        </div>
      </div>
    </div>
  );
}

function TestCard() {
  const [f, setF] = useState({
    street: 'Adolf-Kolping-Str.',
    house: '7',
    postalCode: '85356',
    city: 'Freising',
    name: '',
    postNumber: '',
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));

  const run = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.post(`${API_BASE}/test-address`, f);
      setResult(res.data.data);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Adresse testen" icon={<FlaskConical className="w-5 h-5" />}>
      <p className="text-xs text-[#1A1A1A]/60 mb-4">
        Manuelle Prüfung gegen DHL — schreibt nichts nach Plenty. Bekannte Test-Adressen:
        leitcodierbar = Adolf-Kolping-Str. 7, 85356 Freising · Fehler = Fantasieweg 999, 00000 Nirgendwo.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Field l="Straße (oder Packstation / Filiale)" v={f.street} on={(v) => set('street', v)} /></div>
        <Field l="Hausnr. / Station-Nr." v={f.house} on={(v) => set('house', v)} />
        <Field l="PLZ" v={f.postalCode} on={(v) => set('postalCode', v)} />
        <Field l="Stadt" v={f.city} on={(v) => set('city', v)} />
        <Field l="Postnummer (Packst./Filiale)" v={f.postNumber} on={(v) => set('postNumber', v)} />
      </div>

      <button
        onClick={run}
        disabled={busy}
        className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#1A1A1A] text-white font-semibold text-sm hover:bg-[#1A1A1A]/85 disabled:opacity-50 transition-all"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
        {busy ? 'Prüfe bei DHL…' : 'Adresse prüfen'}
      </button>

      {error && <div className="mt-4 text-sm rounded-lg p-3 bg-red-50 text-red-700">{error}</div>}

      {result && (
        <div
          className={`mt-4 rounded-xl p-4 border ${
            result.codeable ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-center gap-2 font-semibold">
            {result.codeable ? (
              <><ShieldCheck className="w-5 h-5 text-green-600" /> <span className="text-green-700">Leitcodierbar ✓ (würde Tag 189 bekommen)</span></>
            ) : (
              <><ShieldAlert className="w-5 h-5 text-red-600" /> <span className="text-red-700">Nicht leitcodierbar ✗ (würde auf 5.12 + Tag 191)</span></>
            )}
          </div>
          <div className="mt-2 text-xs text-[#1A1A1A]/60">
            Adresstyp: {result.addressType}
          </div>
          {result.problems.length > 0 && (
            <ul className="mt-3 list-disc list-inside text-sm text-red-700 space-y-1">
              {result.problems.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          )}

          {result.messages && result.messages.length > 0 && (
            <details className="mt-4">
              <summary className="text-xs font-semibold text-[#1A1A1A]/70 cursor-pointer">
                Alle DHL-Meldungen ({result.messages.length})
              </summary>
              <table className="w-full mt-2 text-xs">
                <thead>
                  <tr className="text-[#1A1A1A]/50 text-left">
                    <th className="py-1 pr-3 font-medium">Feld</th>
                    <th className="py-1 pr-3 font-medium">Status</th>
                    <th className="py-1 font-medium">Meldung</th>
                  </tr>
                </thead>
                <tbody>
                  {result.messages.map((m, i) => (
                    <tr key={i} className={`border-t border-[#DAD9D9]/50 ${m.relevant ? 'text-red-700 font-medium' : 'text-[#1A1A1A]/55'}`}>
                      <td className="py-1 pr-3 align-top font-mono">{m.property || '—'}</td>
                      <td className="py-1 pr-3 align-top">{m.state}{m.relevant ? ' •' : ''}</td>
                      <td className="py-1 align-top">{m.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[10px] text-[#1A1A1A]/40 mt-1">• = zählt für die Leitcodierbarkeit (Empfänger-Fehler). Absender-/Billing-Meldungen werden ignoriert.</p>
            </details>
          )}
        </div>
      )}
    </Card>
  );
}

type CorrectionMode = 'dry_run' | 'halbautomatisch' | 'vollautomatisch';

interface SettingsData {
  correctionMode: CorrectionMode;
  dryRun: boolean;
  webhookEnabled: boolean;
  pollEnabled: boolean;
  webhookSecret: string;
  webhookUrl: string;
}

interface CorrectionSummary {
  rounds: number;
  changedFields: string[];
  current: { street: string; house: string; postalCode: string; city: string };
  suggested: { street: string; house: string; postalCode: string; city: string };
  googleConfidence: boolean;
  dhlCodeableAfter: boolean;
  resolvedAction: 'log_only' | 'queue_review' | 'write_now' | 'reject_error' | 'giveup';
}

interface CheckResult {
  orderId: number;
  action: string;
  inScope: boolean;
  scopeReason?: string;
  codeable: boolean | null;
  problems: string[];
  dryRun: boolean;
  mode?: CorrectionMode;
  executed: Record<string, boolean>;
  rounds?: number | null;
  correction?: CorrectionSummary | null;
  durationMs: number;
  errorMessage?: string;
}

const ACTION_LABEL: Record<string, { text: string; cls: string }> = {
  ok_tagged: { text: 'Leitcodierbar → Tag 189', cls: 'bg-green-50 text-green-700 border-green-200' },
  error_flagged: { text: 'Fehler → 5.12 + Tag 191 + Notiz + Mail', cls: 'bg-red-50 text-red-700 border-red-200' },
  error_confirmed: { text: 'Fehler bestätigt → zurück auf 5.12 (keine erneute Mail/Notiz)', cls: 'bg-red-50 text-red-700 border-red-200' },
  skipped_scope: { text: 'Übersprungen (kein DHL/DE)', cls: 'bg-[#DAD9D9]/30 text-[#1A1A1A]/60 border-[#DAD9D9]' },
  skipped_already_checked: { text: 'Übersprungen (schon geprüft, Tag 189)', cls: 'bg-[#DAD9D9]/30 text-[#1A1A1A]/60 border-[#DAD9D9]' },
  skipped_no_address: { text: 'Übersprungen (keine Lieferadresse)', cls: 'bg-[#DAD9D9]/30 text-[#1A1A1A]/60 border-[#DAD9D9]' },
  skipped_recent: { text: 'Übersprungen (gerade erst geprüft — Doppel)', cls: 'bg-[#DAD9D9]/30 text-[#1A1A1A]/60 border-[#DAD9D9]' },
  error_dhl: { text: 'Fehler beim Laden/DHL (kein Schreiben)', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  correction_suggested: { text: 'Korrektur-Vorschlag (Dry-Run, DHL-bestätigt)', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  correction_review: { text: 'Korrektur → Freigabe nötig (5.11)', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  correction_applied: { text: 'Korrektur übernommen → Tag 189', cls: 'bg-green-50 text-green-700 border-green-200' },
};

/**
 * Tag-/Ergebnis-Kategorie einer Aktion — Basis für den Tag-Filter in
 * „Jüngste Prüfungen". Bündelt die Aktionen nach dem, was am Auftrag steht
 * (Tag 189 / Tag 191) bzw. dem Bearbeitungszustand (Freigabe/Übersprungen/Fehler).
 */
type TagFilterValue = 'all' | 'tag189' | 'tag191' | 'review' | 'skipped' | 'error';

const TAG_FILTER_OPTIONS: Array<{ value: TagFilterValue; label: string }> = [
  { value: 'all', label: 'Alle Tags' },
  { value: 'tag189', label: 'Tag 189 (leitcodierbar)' },
  { value: 'tag191', label: 'Tag 191 (Adressfehler)' },
  { value: 'review', label: 'Korrektur-Freigabe (5.11)' },
  { value: 'skipped', label: 'Übersprungen' },
  { value: 'error', label: 'Fehler (Laden/DHL)' },
];

function actionTagCategory(action: string): Exclude<TagFilterValue, 'all'> {
  switch (action) {
    case 'ok_tagged':
    case 'correction_applied':
      return 'tag189';
    case 'error_flagged':
    case 'error_confirmed':
      return 'tag191';
    case 'correction_review':
    case 'correction_suggested':
      return 'review';
    case 'error_dhl':
      return 'error';
    default:
      return 'skipped'; // skipped_scope / skipped_already_checked / skipped_no_address / skipped_recent
  }
}

function CheckPanel() {
  const [settings, setSettings] = useState<SettingsData | null>(null);
  const [saving, setSaving] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get(`${API_BASE}/settings`).then((r) => setSettings(r.data.data)).catch(() => {});
  }, []);

  const toggleFlag = async (key: 'webhookEnabled' | 'pollEnabled') => {
    if (!settings || saving) return;
    setSaving(true);
    try {
      const next = !settings[key];
      const r = await api.put(`${API_BASE}/settings`, { [key]: next });
      setSettings((s) => (s ? { ...s, ...r.data.data } : s));
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  const setMode = async (mode: CorrectionMode) => {
    if (!settings || saving || settings.correctionMode === mode) return;
    if (mode === 'vollautomatisch' && !confirm(
      'Vollautomatisch scharf schalten?\n\nDHL-bestätigte Korrekturen werden dann OHNE Klick in echte ' +
      'Plenty-Aufträge geschrieben (gleiche Bedingung wie der „Übernehmen"-Button der Freigabe-Queue). ' +
      'Nur aktivieren, wenn die Halbautomatik belastbar verifiziert ist.',
    )) return;
    setSaving(true);
    try {
      const r = await api.put(`${API_BASE}/settings`, { correctionMode: mode });
      setSettings((s) => (s ? { ...s, ...r.data.data } : s));
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  const runCheck = async () => {
    const id = Number(orderId);
    if (!Number.isFinite(id) || id <= 0) {
      setError('Bitte eine gültige Plenty-Auftragsnummer eingeben.');
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.post(`${API_BASE}/check`, { orderId: id });
      setResult(r.data.data);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const a = result ? ACTION_LABEL[result.action] ?? { text: result.action, cls: 'bg-[#DAD9D9]/30 border-[#DAD9D9]' } : null;

  return (
    <Card title="Modus & Prüfung" icon={<PackageSearch className="w-5 h-5" />}>
      {/* 3-Stufen-Modus */}
      <ModeSwitch
        mode={settings?.correctionMode}
        disabled={!settings || saving}
        onSelect={setMode}
      />

      {/* Webhook + Polling Kill-Switches */}
      <div className="space-y-3 mt-4">
        <ToggleRow
          icon={<Webhook className="w-4 h-4" />}
          title="Webhook-Reaktion"
          on={!!settings?.webhookEnabled}
          disabled={!settings || saving}
          onToggle={() => toggleFlag('webhookEnabled')}
          desc={
            settings?.webhookEnabled
              ? 'AN — eingehende Plenty-Aufrufe (Statuswechsel 5.1/5.2/5.01) werden geprüft.'
              : 'AUS — eingehende Webhook-Aufrufe werden ignoriert (keine Prüfung).'
          }
        />
        <ToggleRow
          icon={<PackageSearch className="w-4 h-4" />}
          title="Polling"
          on={!!settings?.pollEnabled}
          disabled={!settings || saving}
          onToggle={() => toggleFlag('pollEnabled')}
          desc={
            settings?.pollEnabled
              ? 'AN — der Polling-Lauf prüft offene Aufträge (nur wenn der Schedule zusätzlich aktiv ist).'
              : 'AUS — der Polling-Lauf tut nichts, selbst wenn der Schedule feuert.'
          }
        />
      </div>

      {/* Einzelnen Plenty-Auftrag manuell prüfen (z.B. zum Gegenprüfen). */}
      <div className="mt-5">
        <label className={label}>Echten Plenty-Auftrag prüfen (lädt Adresse + Versandprofil selbst)</label>
        <div className="flex gap-2">
          <input
            className={input}
            value={orderId}
            placeholder="Auftragsnummer, z.B. 278949"
            onChange={(e) => setOrderId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runCheck()}
          />
          <button
            onClick={runCheck}
            disabled={busy}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#1A1A1A] text-white font-semibold text-sm hover:bg-[#1A1A1A]/85 disabled:opacity-50 whitespace-nowrap"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageSearch className="w-4 h-4" />}
            {busy ? 'Prüfe…' : 'Auftrag prüfen'}
          </button>
        </div>
      </div>

      {error && <div className="mt-4 text-sm rounded-lg p-3 bg-red-50 text-red-700">{error}</div>}

      {result && a && (
        <div className={`mt-4 rounded-xl p-4 border ${a.cls}`}>
          <div className="font-semibold">
            Auftrag {result.orderId}: {a.text}
            {result.dryRun && <span className="ml-2 text-xs font-normal opacity-70">(Dry-Run — nichts geschrieben)</span>}
          </div>
          {result.scopeReason && <div className="mt-1 text-xs opacity-70">Scope: {result.scopeReason}</div>}
          {result.problems.length > 0 && (
            <ul className="mt-2 list-disc list-inside text-sm space-y-1">
              {result.problems.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          )}
          {result.correction && (
            <div className="mt-3 rounded-lg bg-white/60 border border-current/10 p-3">
              <AddressDiff
                current={result.correction.current}
                suggested={result.correction.suggested}
                changedFields={result.correction.changedFields}
              />
              <div className="mt-2 text-[11px] opacity-70">
                Google-Confidence: {result.correction.googleConfidence ? 'hoch (CONFIRMED)' : 'niedrig'} ·
                DHL nach Korrektur: {result.correction.dhlCodeableAfter ? 'leitcodierbar ✓' : 'weiterhin Fehler'} ·
                Runden: {result.correction.rounds}
              </div>
            </div>
          )}
          {result.errorMessage && <div className="mt-2 text-xs">{result.errorMessage}</div>}
          <div className="mt-2 text-[11px] opacity-60">Dauer: {result.durationMs} ms{result.mode ? ` · Modus: ${result.mode}` : ''}</div>
        </div>
      )}

      {/* Webhook-URL für den Plenty-Flow-Studio-Flow „URL aufrufen". */}
      {settings && (
        <div className="mt-5 rounded-xl border border-[#DAD9D9] bg-[#F9F9F7] p-4">
          <div className="font-semibold text-sm text-[#1A1A1A] flex items-center gap-2 mb-1">
            <Webhook className="w-4 h-4" /> Webhook-URL für Plenty (Flow Studio „URL aufrufen")
          </div>
          <p className="text-xs text-[#1A1A1A]/60 mb-2">
            Diese URL ruft der Plenty-Flow-Studio-Flow „Statuswechsel auf 5.1 / 5.2 / 5.01" auf (GET, Aktion „URL aufrufen").
            Die Auftragsnummer per Platzhalter-Chip <code>$OrderID</code> einsetzen (NICHT tippen). Das Secret ist
            token-geschützt — nicht öffentlich teilen.
          </p>
          <code className="block text-[11px] break-all bg-white border border-[#DAD9D9] rounded-lg p-2 text-[#1A1A1A]/80">
            {settings.webhookUrl}
          </code>
        </div>
      )}
    </Card>
  );
}

interface DashboardData {
  windowDays: number;
  dryRun: boolean;
  mode?: CorrectionMode;
  stats: {
    total: number;
    today: number;
    byAction: Record<string, number>;
    okCount: number;
    errorCount: number;
    skippedCount: number;
    dhlErrorCount: number;
    correctedCount: number;
    reviewCount: number;
    suggestedCount: number;
    okRate: number | null;
    errorRate: number | null;
    topErrors: { message: string; count: number }[];
  };
  recent: {
    id: number;
    orderId: number;
    source: string;
    action: string;
    codeable: boolean | null;
    dryRun: boolean;
    street: string;
    house: string;
    postalCode: string;
    city: string;
    statusSince: string | null;
    problems: string[];
    softProblems: string[];
    errorMessage: string | null;
    rounds: number | null;
    googleCalled: boolean;
    correction: CorrectionSummary | null;
    createdAt: string;
  }[];
}

function DashboardSection() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (d: number) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`${API_BASE}/dashboard`, { params: { days: d } });
      setData(r.data.data);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const s = data?.stats;

  return (
    <Card
      title="Auswertung"
      icon={<BarChart3 className="w-5 h-5" />}
    >
      <div className="flex items-center justify-between mb-3 -mt-1">
        <div className="flex gap-1.5">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                days === d ? 'bg-[#1A1A1A] text-white' : 'bg-[#DAD9D9]/40 text-[#1A1A1A]/70 hover:bg-[#DAD9D9]/70'
              }`}
            >
              {d} Tage
            </button>
          ))}
        </div>
        <button
          onClick={() => load(days)}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-[#1A1A1A]/60 hover:text-[#1A1A1A] disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
        </button>
      </div>

      {error && <div className="text-sm rounded-lg p-3 bg-red-50 text-red-700">{error}</div>}

      {s && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Heute geprüft" value={String(s.today)} icon={<CheckCircle2 className="w-4 h-4" />} />
            <StatCard
              label="OK-Quote"
              value={s.okRate != null ? `${s.okRate}%` : '–'}
              sub={`${s.okCount} leitcodierbar`}
              tone="green"
            />
            <StatCard
              label="Fehlerquote"
              value={s.errorRate != null ? `${s.errorRate}%` : '–'}
              sub={`${s.errorCount} Adressfehler`}
              tone={s.errorCount > 0 ? 'red' : undefined}
            />
            <StatCard
              label={`Gesamt (${data!.windowDays} T.)`}
              value={String(s.total)}
              sub={`${s.skippedCount} übersprungen${s.dhlErrorCount ? ` · ${s.dhlErrorCount} DHL-Fehler` : ''}`}
            />
          </div>

          {/* Korrektur-Kennzahlen (Phase 6) — nur wenn es Korrektur-Aktivität gab */}
          {(s.correctedCount + s.reviewCount + s.suggestedCount) > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
              <StatCard label="Korrigiert (auto)" value={String(s.correctedCount)} sub="Adresse geschrieben → Tag 189" tone={s.correctedCount > 0 ? 'green' : undefined} icon={<Wand2 className="w-4 h-4" />} />
              <StatCard label="Wartet auf Freigabe" value={String(s.reviewCount)} sub="in der Korrektur-Freigabe (5.11)" icon={<Wand2 className="w-4 h-4" />} />
              <StatCard label="Vorschläge (Dry-Run)" value={String(s.suggestedCount)} sub="DHL-bestätigt, nicht geschrieben" icon={<Wand2 className="w-4 h-4" />} />
            </div>
          )}

          {/* Häufigste Fehlerarten */}
          {s.topErrors.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-[#1A1A1A] mb-2">Häufigste Fehlerarten</h4>
              <div className="space-y-1.5">
                {s.topErrors.map((e, i) => {
                  const max = s.topErrors[0].count || 1;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className="flex-1 h-6 rounded bg-[#DAD9D9]/30 relative overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-red-200"
                          style={{ width: `${Math.round((e.count / max) * 100)}%` }}
                        />
                        <span className="absolute inset-0 flex items-center px-2 text-xs text-[#1A1A1A]/90 truncate">
                          {e.message}
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-[#1A1A1A]/70 w-8 text-right">{e.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Jüngste Prüfungen — pro Auftrag gruppiert, nach Tag filterbar, scrollbar */}
          <RecentChecks recent={data!.recent} />
        </>
      )}

      {!s && !error && loading && (
        <div className="flex items-center gap-2 text-sm text-[#1A1A1A]/50 py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Lade Auswertung…
        </div>
      )}
    </Card>
  );
}

type RecentRow = DashboardData['recent'][number];

/** Tagesschlüssel (YYYY-MM-DD) in Berlin-Zeit. */
function berlinDayKey(d: string | Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(d));
}

/** Sprechendes Label für einen Tagesschlüssel (Heute / Gestern / DD.MM.YYYY). */
function dayLabel(key: string): string {
  const today = berlinDayKey(new Date());
  const yesterday = berlinDayKey(new Date(Date.now() - 86_400_000));
  if (key === today) return 'Heute';
  if (key === yesterday) return 'Gestern';
  const [y, m, d] = key.split('-');
  return `${d}.${m}.${y}`;
}

function recentTime(d: string): string {
  return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function shortDate(d: string): string {
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * „Jüngste Prüfungen": gruppiert pro Auftrag (eine Zeile je Auftrag mit
 * aktuellem Stand + Anzahl der Prüfungen, aufklappbar für die Historie),
 * nach Tag filterbar und in sich scrollbar — damit ein mehrfach geprüfter
 * Auftrag (z.B. ein Problemauftrag, der über Stunden immer wieder in 5.1 lag)
 * nicht die ganze Liste flutet.
 */
function RecentChecks({ recent }: { recent: RecentRow[] }) {
  const [tagFilter, setTagFilter] = useState<TagFilterValue>('all');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Verlauf: Tages-Auswahl serverseitig. 'recent' = die jüngsten ~200 Prüfungen
  // (Standard); ein konkreter Tag (YYYY-MM-DD) lädt ALLE Prüfungen dieses
  // Berlin-Tages nach — auch ältere, die längst aus dem 200er-Fenster gefallen
  // sind. So lässt sich der komplette Verlauf jedes Tages nachschauen.
  const [dayOptions, setDayOptions] = useState<string[]>([]);
  const [selectedDay, setSelectedDay] = useState<string>('recent');
  const [dayRows, setDayRows] = useState<RecentRow[] | null>(null);
  const [dayLoading, setDayLoading] = useState(false);

  useEffect(() => {
    api.get(`${API_BASE}/checks/days`).then((r) => setDayOptions(r.data.data.days as string[])).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedDay === 'recent') {
      setDayRows(null);
      return;
    }
    let cancelled = false;
    setDayLoading(true);
    api
      .get(`${API_BASE}/checks/by-day`, { params: { date: selectedDay } })
      .then((r) => { if (!cancelled) setDayRows(r.data.data.rows as RecentRow[]); })
      .catch(() => { if (!cancelled) setDayRows([]); })
      .finally(() => { if (!cancelled) setDayLoading(false); });
    return () => { cancelled = true; };
  }, [selectedDay]);

  // Auftragssuche: holt ALLE Prüfungen zu einer Auftragsnummer (fensterunabhängig).
  // Behebt, dass ältere Prüfungen aus dem 200er-Dashboard-Fenster fallen.
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<RecentRow[] | null>(null);
  const [searchedOrder, setSearchedOrder] = useState<number | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const runSearch = async () => {
    const orderId = parseInt(searchInput.trim(), 10);
    if (!Number.isFinite(orderId) || orderId <= 0) {
      setSearchError('Bitte eine gültige Auftragsnummer eingeben.');
      return;
    }
    setSearching(true);
    setSearchError(null);
    try {
      const r = await api.get(`${API_BASE}/checks/search`, { params: { orderId } });
      setSearchResults(r.data.data.rows as RecentRow[]);
      setSearchedOrder(orderId);
    } catch (e: any) {
      setSearchError(e?.response?.data?.error?.message || e.message);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchResults(null);
    setSearchedOrder(null);
    setSearchInput('');
    setSearchError(null);
  };

  const toggle = (orderId: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(orderId) ? next.delete(orderId) : next.add(orderId);
      return next;
    });

  // Aktive Suche überschreibt die Tagesauswahl; sonst entweder die jüngsten
  // Prüfungen ('recent') oder die komplett vom Server nachgeladenen Prüfungen
  // des gewählten Tages.
  const base = searchResults ?? (selectedDay === 'recent' ? recent : dayRows ?? []);
  const filtered = base;

  // Pro Auftrag gruppieren. `recent` ist bereits nach Zeit absteigend → der
  // erste Treffer je Auftrag ist die jüngste Prüfung, Map-Insertion-Reihenfolge
  // bleibt nach jüngster Prüfung absteigend.
  const groups = new Map<number, RecentRow[]>();
  for (const r of filtered) {
    const arr = groups.get(r.orderId);
    if (arr) arr.push(r);
    else groups.set(r.orderId, [r]);
  }
  // Tag-Filter wirkt auf den AKTUELLEN Stand je Auftrag (= jüngste Prüfung,
  // checks[0]) — passend zur Spalte „Aktueller Stand". In der Auftragssuche
  // ist der Filter aus (dort geht es um genau einen Auftrag).
  const allGroups = [...groups.values()];
  const orderGroups =
    searchResults || tagFilter === 'all'
      ? allGroups
      : allGroups.filter((checks) => actionTagCategory(checks[0].action) === tagFilter);
  const visibleChecks = orderGroups.reduce((n, g) => n + g.length, 0);

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <h4 className="text-sm font-semibold text-[#1A1A1A]">
          {searchResults
            ? `Prüfungen für Auftrag ${searchedOrder}`
            : selectedDay === 'recent'
              ? 'Jüngste Prüfungen'
              : `Prüfungen am ${dayLabel(selectedDay)}`}
          <span className="ml-2 text-xs font-normal text-[#1A1A1A]/45">
            {dayLoading
              ? 'lädt…'
              : `${orderGroups.length} ${orderGroups.length === 1 ? 'Auftrag' : 'Aufträge'}${
                  visibleChecks !== orderGroups.length ? ` · ${visibleChecks} Prüfungen` : ''
                }`}
          </span>
        </h4>
        <div className="flex items-center gap-2">
          {/* Suche nach Auftragsnummer (fensterunabhängig) */}
          <div className="flex items-center gap-1">
            <input
              type="text"
              inputMode="numeric"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              placeholder="Auftragsnr. suchen"
              className="text-xs rounded-lg border border-[#DAD9D9] bg-white px-2 py-1 w-36 text-[#1A1A1A]/80 focus:outline-none focus:border-[#1A1A1A]"
            />
            <button
              onClick={runSearch}
              disabled={searching}
              className="text-xs font-semibold rounded-lg bg-[#1A1A1A] text-white px-2.5 py-1 hover:bg-[#1A1A1A]/85 disabled:opacity-50"
            >
              {searching ? '…' : 'Suchen'}
            </button>
            {searchResults && (
              <button
                onClick={clearSearch}
                className="text-xs text-[#1A1A1A]/55 hover:text-[#1A1A1A] px-1.5 py-1"
                title="Suche zurücksetzen"
              >
                ✕
              </button>
            )}
          </div>
          {!searchResults && (
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value as TagFilterValue)}
              className="text-xs rounded-lg border border-[#DAD9D9] bg-white px-2 py-1 text-[#1A1A1A]/80 focus:outline-none focus:border-[#1A1A1A]"
            >
              {TAG_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          )}
          {!searchResults && (
            <select
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
              className="text-xs rounded-lg border border-[#DAD9D9] bg-white px-2 py-1 text-[#1A1A1A]/80 focus:outline-none focus:border-[#1A1A1A]"
              title="Tag wählen — lädt den kompletten Verlauf dieses Tages"
            >
              <option value="recent">Jüngste</option>
              {dayOptions.map((k) => (
                <option key={k} value={k}>
                  {dayLabel(k)}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {searchError && <p className="text-sm rounded-lg p-2 mb-2 bg-red-50 text-red-700">{searchError}</p>}

      {dayLoading ? (
        <p className="text-sm text-[#1A1A1A]/50 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Lade Verlauf vom {dayLabel(selectedDay)}…
        </p>
      ) : orderGroups.length === 0 ? (
        <p className="text-sm text-[#1A1A1A]/50">
          {searchResults
            ? `Keine Prüfung für Auftrag ${searchedOrder} protokolliert.`
            : tagFilter !== 'all'
              ? 'Keine Aufträge mit diesem Tag in diesem Zeitraum.'
              : 'Keine Prüfungen in diesem Zeitraum.'}
        </p>
      ) : (
        <div className="rounded-xl border border-[#DAD9D9] overflow-hidden">
          <div className="max-h-[480px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#F1F1EE] text-[#1A1A1A]/60 text-left">
                  <th className="px-3 py-2 font-semibold w-6"></th>
                  <th className="px-3 py-2 font-semibold">Zeit</th>
                  <th className="px-3 py-2 font-semibold">Auftrag</th>
                  <th className="px-3 py-2 font-semibold">Im Status seit</th>
                  <th className="px-3 py-2 font-semibold">Aktueller Stand</th>
                  <th className="px-3 py-2 font-semibold">Adresse</th>
                  <th className="px-3 py-2 font-semibold">Quelle</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Tages-Trennzeile vor dem ersten Auftrag eines neuen Tages —
                  // damit bei „Alle Tage" sofort sichtbar ist, dass mehrere Tage
                  // enthalten sind (sonst dominiert der jüngste Tag oben die Sicht).
                  // In der Auftragssuche (ein Auftrag) weggelassen.
                  let lastDay = '';
                  const out: ReactNode[] = [];
                  for (const checks of orderGroups) {
                    const latest = checks[0];
                    const day = berlinDayKey(latest.createdAt);
                    if (!searchResults && day !== lastDay) {
                      lastDay = day;
                      out.push(
                        <tr key={`day-${day}`} className="bg-[#F1F1EE]/60">
                          <td colSpan={7} className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#1A1A1A]/45">
                            {dayLabel(day)}
                          </td>
                        </tr>,
                      );
                    }
                    out.push(
                      <CheckGroupRows
                        key={latest.orderId}
                        latest={latest}
                        checks={checks}
                        count={checks.length}
                        isOpen={expanded.has(latest.orderId)}
                        actionLabel={ACTION_LABEL[latest.action] ?? { text: latest.action, cls: '' }}
                        onToggle={() => toggle(latest.orderId)}
                      />,
                    );
                  }
                  return out;
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function CheckGroupRows({
  latest,
  checks,
  count,
  isOpen,
  actionLabel,
  onToggle,
}: {
  latest: RecentRow;
  checks: RecentRow[];
  count: number;
  isOpen: boolean;
  actionLabel: { text: string; cls: string };
  onToggle: () => void;
}) {
  const hasHistory = count > 1;
  return (
    <>
      <tr
        className={`border-t border-[#DAD9D9]/50 align-top ${hasHistory ? 'cursor-pointer hover:bg-[#DAD9D9]/15' : ''}`}
        onClick={hasHistory ? onToggle : undefined}
      >
        <td className="px-3 py-2 text-[#1A1A1A]/40">
          {hasHistory && (isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
        </td>
        <td className="px-3 py-2 text-[#1A1A1A]/55 whitespace-nowrap">{recentTime(latest.createdAt)}</td>
        <td className="px-3 py-2 font-mono">
          {latest.orderId}
          {hasHistory && (
            <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded-full bg-[#DAD9D9]/50 text-[10px] text-[#1A1A1A]/60 font-sans">
              {count} Prüfungen
            </span>
          )}
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          {latest.statusSince ? (
            <span
              className="inline-block px-2 py-0.5 rounded-full bg-[#DAD9D9]/40 text-[11px] text-[#1A1A1A]/60"
              title="Datum des Wechsels in den aktuellen Plenty-Status"
            >
              seit {shortDate(latest.statusSince)}
            </span>
          ) : (
            <span className="text-[#1A1A1A]/30">–</span>
          )}
        </td>
        <td className="px-3 py-2">
          <span className={`inline-block px-2 py-0.5 rounded border text-[11px] ${actionLabel.cls}`}>{actionLabel.text}</span>
          {latest.dryRun && <span className="ml-1 text-[10px] text-[#1A1A1A]/40">dry</span>}
          {latest.problems.length > 0 && <div className="mt-1 text-[#1A1A1A]/60">{latest.problems.join('; ')}</div>}
          {latest.softProblems.length > 0 && (
            <div className="mt-1 text-[11px] text-amber-700">Ort weicht ab — laut DHL lieferbar ({latest.softProblems.join('; ')})</div>
          )}
          {latest.errorMessage && !latest.problems.length && <div className="mt-1 text-[#1A1A1A]/45">{latest.errorMessage}</div>}
        </td>
        <td className="px-3 py-2 text-[#1A1A1A]/70">
          {latest.street ? `${latest.street} ${latest.house}, ${latest.postalCode} ${latest.city}` : '–'}
          {latest.correction && latest.correction.changedFields.length > 0 && (
            <div className="mt-1 text-[11px] text-green-700 flex items-center gap-1">
              <ArrowRight className="w-3 h-3" />
              {latest.correction.suggested.street} {latest.correction.suggested.house}, {latest.correction.suggested.postalCode}{' '}
              {latest.correction.suggested.city}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-[#1A1A1A]/55">
          {latest.source}
          {latest.googleCalled && <span className="block text-[10px] text-blue-600">Google{latest.rounds ? ` ·${latest.rounds}R` : ''}</span>}
        </td>
      </tr>
      {isOpen &&
        checks.slice(1).map((r) => {
          const a = ACTION_LABEL[r.action] ?? { text: r.action, cls: '' };
          return (
            <tr key={r.id} className="border-t border-[#DAD9D9]/30 align-top bg-[#F9F9F7]/60 text-[#1A1A1A]/70">
              <td className="px-3 py-1.5"></td>
              <td className="px-3 py-1.5 text-[#1A1A1A]/45 whitespace-nowrap">{recentTime(r.createdAt)}</td>
              <td className="px-3 py-1.5"></td>
              <td className="px-3 py-1.5"></td>
              <td className="px-3 py-1.5">
                <span className={`inline-block px-2 py-0.5 rounded border text-[11px] ${a.cls}`}>{a.text}</span>
                {r.dryRun && <span className="ml-1 text-[10px] text-[#1A1A1A]/40">dry</span>}
                {r.problems.length > 0 && <div className="mt-1 text-[#1A1A1A]/55">{r.problems.join('; ')}</div>}
                {r.errorMessage && !r.problems.length && <div className="mt-1 text-[#1A1A1A]/40">{r.errorMessage}</div>}
              </td>
              <td className="px-3 py-1.5 text-[#1A1A1A]/55">
                {r.street ? `${r.street} ${r.house}, ${r.postalCode} ${r.city}` : '–'}
              </td>
              <td className="px-3 py-1.5 text-[#1A1A1A]/45">
                {r.source}
                {r.googleCalled && <span className="block text-[10px] text-blue-600">Google{r.rounds ? ` ·${r.rounds}R` : ''}</span>}
              </td>
            </tr>
          );
        })}
    </>
  );
}

const MODE_INFO: Record<CorrectionMode, { title: string; desc: string; tone: string; active: string }> = {
  dry_run: {
    title: 'Dry-Run',
    desc: 'Prüft + protokolliert, schreibt NICHTS nach Plenty (keine Tags/Status/Mail). Sicher zum Beobachten.',
    tone: 'text-green-700',
    active: 'bg-green-500 text-white border-green-500',
  },
  halbautomatisch: {
    title: 'Halbautomatisch',
    desc: 'Saubere Adressen → Tag 189 auto, Totalausfall → 5.12+191 auto. Korrekturen gehen in die Korrektur-Freigabe und werden erst per Klick übernommen.',
    tone: 'text-amber-700',
    active: 'bg-amber-500 text-white border-amber-500',
  },
  vollautomatisch: {
    title: 'Vollautomatisch',
    desc: 'Wie halbautomatisch — aber DHL-bestätigte Korrekturen werden OHNE Klick geschrieben (erscheinen unten in „Korrigiert" als „automatisch").',
    tone: 'text-red-700',
    active: 'bg-red-500 text-white border-red-500',
  },
};

function ModeSwitch({ mode, disabled, onSelect }: { mode?: CorrectionMode; disabled?: boolean; onSelect: (m: CorrectionMode) => void }) {
  const modes: CorrectionMode[] = ['dry_run', 'halbautomatisch', 'vollautomatisch'];
  const current = mode ? MODE_INFO[mode] : null;
  return (
    <div>
      <label className={label}>Betriebsmodus</label>
      <div className="grid grid-cols-3 gap-2">
        {modes.map((m) => {
          const info = MODE_INFO[m];
          const isActive = mode === m;
          return (
            <button
              key={m}
              onClick={() => onSelect(m)}
              disabled={disabled}
              className={`px-3 py-2.5 rounded-xl border text-sm font-semibold transition-all disabled:opacity-50 ${
                isActive ? info.active : 'bg-white border-[#DAD9D9] text-[#1A1A1A]/70 hover:border-[#1A1A1A]/40'
              }`}
            >
              {info.title}
            </button>
          );
        })}
      </div>
      {current && (
        <p className={`text-xs mt-2 ${current.tone}`}>{current.desc}</p>
      )}
      {mode === 'vollautomatisch' && (
        <div className="mt-2 text-xs rounded-lg p-2 bg-red-50 text-red-700 font-medium">
          ⚠️ Vollautomatisch — DHL-bestätigte Korrekturen werden ohne Freigabe in echte Plenty-Aufträge geschrieben.
        </div>
      )}
    </div>
  );
}

const FIELD_LABEL: Record<string, string> = { street: 'Straße', house: 'Hausnr.', postalCode: 'PLZ', city: 'Ort' };

function AddressDiff({
  current,
  suggested,
  changedFields,
}: {
  current: { street: string; house: string; postalCode: string; city: string };
  suggested: { street: string; house: string; postalCode: string; city: string };
  changedFields: string[];
}) {
  const fields: Array<keyof typeof current> = ['street', 'house', 'postalCode', 'city'];
  return (
    <table className="w-full text-xs">
      <tbody>
        {fields.map((f) => {
          const changed = changedFields.includes(f);
          return (
            <tr key={f} className={changed ? 'font-semibold' : 'text-[#1A1A1A]/60'}>
              <td className="py-0.5 pr-3 text-[#1A1A1A]/50 w-16">{FIELD_LABEL[f]}</td>
              <td className="py-0.5 pr-2">{current[f] || '—'}</td>
              <td className="py-0.5 px-1 text-[#1A1A1A]/40">
                {changed ? <ArrowRight className="w-3 h-3 inline" /> : ''}
              </td>
              <td className={`py-0.5 ${changed ? 'text-green-700' : 'text-[#1A1A1A]/40'}`}>
                {changed ? (suggested[f] || '—') : ''}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

interface CorrectionRow {
  id: number;
  orderId: number;
  originalStatus: number;
  currentAddress: { street: string; house: string; postalCode: string; city: string } | null;
  suggestedAddress: { street: string; house: string; postalCode: string; city: string } | null;
  changedFields: string[] | null;
  googleConfidence: boolean;
  dhlCodeableAfter: boolean;
  rounds: number;
  status: string;
  appliedAt: string | null;
  appliedBy: number | null;
  createdAt: string;
}

function ReviewQueue() {
  const [rows, setRows] = useState<CorrectionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`${API_BASE}/corrections`, { params: { status: 'pending' } });
      setRows(r.data.data ?? []);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = async (orderId: number) => {
    setBusyId(orderId);
    setError(null);
    setMsg(null);
    try {
      const r = await api.post(`${API_BASE}/corrections/${orderId}/apply`);
      setMsg(r.data?.data?.message || `Auftrag ${orderId}: Korrektur übernommen.`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.data?.message || e?.response?.data?.error?.message || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (orderId: number) => {
    if (!confirm(`Korrektur-Vorschlag für Auftrag ${orderId} ablehnen? Der Auftrag bleibt in Status 5.11 (manuell weiterbearbeiten).`)) return;
    setBusyId(orderId);
    setError(null);
    setMsg(null);
    try {
      await api.post(`${API_BASE}/corrections/${orderId}/reject`);
      setMsg(`Auftrag ${orderId}: Vorschlag abgelehnt.`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card title={`Korrektur-Freigabe${rows.length ? ` (${rows.length})` : ''}`} icon={<Wand2 className="w-5 h-5" />}>
      <div className="flex items-center justify-between -mt-1 mb-3">
        <p className="text-xs text-[#1A1A1A]/60">
          DHL-bestätigte Google-Korrekturen, die auf Freigabe warten (Auftrag steht in <strong>5.11</strong>).
          „Übernehmen" schreibt die Adresse, setzt den Auftrag zurück in den Ursprungsstatus und taggt 189.
        </p>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-[#1A1A1A]/60 hover:text-[#1A1A1A] disabled:opacity-50 shrink-0 ml-3"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
        </button>
      </div>

      {error && <div className="text-sm rounded-lg p-3 bg-red-50 text-red-700 mb-3">{error}</div>}
      {msg && <div className="text-sm rounded-lg p-3 bg-green-50 text-green-700 mb-3">{msg}</div>}

      {rows.length === 0 ? (
        <p className="text-sm text-[#1A1A1A]/50 py-2">Keine offenen Korrektur-Freigaben.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((c) => (
            <div key={c.id} className="rounded-xl border border-[#DAD9D9] p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-[#1A1A1A]">
                    Auftrag <span className="font-mono">{c.orderId}</span>
                    <span className="ml-2 text-xs font-normal text-[#1A1A1A]/50">
                      Ursprungsstatus {c.originalStatus} · Runde {c.rounds}
                    </span>
                  </div>
                  <div className="mt-2">
                    {c.currentAddress && c.suggestedAddress && (
                      <AddressDiff
                        current={c.currentAddress}
                        suggested={c.suggestedAddress}
                        changedFields={c.changedFields ?? []}
                      />
                    )}
                  </div>
                  <div className="mt-2 text-[11px] text-[#1A1A1A]/55 flex flex-wrap gap-x-3">
                    <span>Google-Confidence: <strong className={c.googleConfidence ? 'text-green-700' : 'text-amber-700'}>{c.googleConfidence ? 'hoch' : 'niedrig'}</strong></span>
                    <span>DHL nach Korrektur: <strong className={c.dhlCodeableAfter ? 'text-green-700' : 'text-red-700'}>{c.dhlCodeableAfter ? 'leitcodierbar ✓' : 'Fehler'}</strong></span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    onClick={() => apply(c.orderId)}
                    disabled={busyId === c.orderId || !c.dhlCodeableAfter}
                    title={c.dhlCodeableAfter ? 'Korrektur in Plenty übernehmen' : 'DHL hat den Vorschlag nicht bestätigt'}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1A1A1A] text-white text-sm font-semibold hover:bg-[#1A1A1A]/85 disabled:opacity-40"
                  >
                    {busyId === c.orderId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Übernehmen
                  </button>
                  <button
                    onClick={() => reject(c.orderId)}
                    disabled={busyId === c.orderId}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#DAD9D9] text-[#1A1A1A]/70 text-sm font-medium hover:border-red-300 hover:text-red-600 disabled:opacity-40"
                  >
                    <X className="w-4 h-4" /> Ablehnen
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/**
 * „Korrigiert (übernommen)": alle Adress-Korrekturen, die tatsächlich nach
 * Plenty geschrieben wurden (status='applied') — egal ob halbautomatisch per
 * Klick (appliedBy gesetzt) oder vollautomatisch (appliedBy=null). Gibt einen
 * schnellen Überblick, welche Aufträge das Tool repariert hat.
 */
function AppliedCorrections() {
  const [rows, setRows] = useState<CorrectionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`${API_BASE}/corrections`, { params: { status: 'applied', limit: 200 } });
      setRows(r.data.data ?? []);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card title={`Korrigiert${rows.length ? ` (${rows.length})` : ''}`} icon={<ClipboardCheck className="w-5 h-5" />}>
      <div className="flex items-center justify-between -mt-1 mb-3">
        <p className="text-xs text-[#1A1A1A]/60">
          Aufträge, bei denen die Adresse erfolgreich übernommen wurde — <strong>per Klick</strong> (halbautomatisch) oder{' '}
          <strong>automatisch</strong> (vollautomatisch). DHL hat jeden Vorschlag vorher bestätigt.
        </p>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-[#1A1A1A]/60 hover:text-[#1A1A1A] disabled:opacity-50 shrink-0 ml-3"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
        </button>
      </div>

      {error && <div className="text-sm rounded-lg p-3 bg-red-50 text-red-700 mb-3">{error}</div>}

      {rows.length === 0 ? (
        <p className="text-sm text-[#1A1A1A]/50 py-2">Noch keine übernommenen Korrekturen.</p>
      ) : (
        <div className="rounded-xl border border-[#DAD9D9] overflow-hidden">
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#F1F1EE] text-[#1A1A1A]/60 text-left">
                  <th className="px-3 py-2 font-semibold">Übernommen</th>
                  <th className="px-3 py-2 font-semibold">Auftrag</th>
                  <th className="px-3 py-2 font-semibold">Korrektur (alt → neu)</th>
                  <th className="px-3 py-2 font-semibold">Wie</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const auto = c.appliedBy == null;
                  const cur = c.currentAddress;
                  const sug = c.suggestedAddress;
                  return (
                    <tr key={c.id} className="border-t border-[#DAD9D9]/50 align-top">
                      <td className="px-3 py-2 text-[#1A1A1A]/55 whitespace-nowrap">
                        {c.appliedAt ? recentTime(c.appliedAt) : recentTime(c.createdAt)}
                      </td>
                      <td className="px-3 py-2 font-mono">{c.orderId}</td>
                      <td className="px-3 py-2 text-[#1A1A1A]/70">
                        {cur && <div className="text-[#1A1A1A]/50 line-through">{`${cur.street} ${cur.house}, ${cur.postalCode} ${cur.city}`}</div>}
                        {sug && (
                          <div className="text-green-700 flex items-center gap-1">
                            <ArrowRight className="w-3 h-3" />
                            {`${sug.street} ${sug.house}, ${sug.postalCode} ${sug.city}`}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block px-2 py-0.5 rounded border text-[11px] ${
                            auto ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-800 border-amber-200'
                          }`}
                        >
                          {auto ? 'automatisch ✓' : 'per Klick ✓'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

/** Box in der oberen Prozess-Pipeline. */
function FlowBox({ title, sub, tone = 'plain' }: { title: string; sub: string; tone?: 'plain' | 'dark' | 'blue' }) {
  const cls =
    tone === 'dark'
      ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
      : tone === 'blue'
        ? 'bg-blue-50 text-blue-800 border-blue-200'
        : 'bg-[#F9F9F7] text-[#1A1A1A] border-[#DAD9D9]';
  return (
    <div className={`flex-1 rounded-lg border px-3 py-2 ${cls}`}>
      <div className="text-xs font-semibold">{title}</div>
      <div className={`text-[10px] leading-tight mt-0.5 ${tone === 'dark' ? 'text-white/70' : 'opacity-70'}`}>{sub}</div>
    </div>
  );
}

/** Farbcodierte Ergebnis-Karte (Endzustand). */
function OutcomeCard({
  tone,
  badges,
  title,
  sub,
}: {
  tone: 'green' | 'violet' | 'red' | 'gray';
  badges: string[];
  title: string;
  sub: string;
}) {
  const map = {
    green: { box: 'border-green-200 bg-green-50', badge: 'bg-green-100 text-green-700' },
    violet: { box: 'border-violet-200 bg-violet-50', badge: 'bg-violet-100 text-violet-700' },
    red: { box: 'border-red-200 bg-red-50', badge: 'bg-red-100 text-red-700' },
    gray: { box: 'border-[#DAD9D9] bg-[#DAD9D9]/20', badge: 'bg-[#DAD9D9]/60 text-[#1A1A1A]/60' },
  }[tone];
  return (
    <div className={`rounded-lg border px-3 py-2 ${map.box}`}>
      <div className="flex items-center gap-1 flex-wrap mb-1">
        {badges.map((b) => (
          <span key={b} className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${map.badge}`}>{b}</span>
        ))}
      </div>
      <div className="text-xs font-semibold text-[#1A1A1A]">{title}</div>
      <div className="text-[10px] leading-tight text-[#1A1A1A]/60 mt-0.5">{sub}</div>
    </div>
  );
}

function WorkflowDiagram() {
  const arrow = <ArrowRight className="w-4 h-4 text-[#1A1A1A]/30 shrink-0 self-center rotate-90 sm:rotate-0" />;
  return (
    <div className="bg-white rounded-2xl border border-[#DAD9D9] p-4">
      <h3 className="text-base font-semibold text-[#1A1A1A] flex items-center gap-2 mb-1">
        <Workflow className="w-5 h-5 text-[#1A1A1A]/70" /> Ablauf auf einen Blick
      </h3>
      <p className="text-xs text-[#1A1A1A]/60 mb-3">
        Trigger → DHL prüft die Adresse → bei Fehler holt Google einen Vorschlag, der <strong>erneut gegen DHL</strong> geprüft wird. DHL ist immer der letzte Richter.
      </p>

      {/* Prozess-Pipeline */}
      <div className="flex flex-col sm:flex-row gap-1.5 mb-4">
        <FlowBox title="1 · Auslöser" sub="Status 5.1 / 5.2 / 5.01 · Webhook + Polling" />
        {arrow}
        <FlowBox title="2 · DHL-Leitcodierung" sub="Lieferadresse · nur DHL-Versand + DE" tone="dark" />
        {arrow}
        <FlowBox title="3 · Google-Korrektur" sub="bei Fehler · Vorschlag → erneut DHL · max. 2 Runden" tone="blue" />
      </div>

      {/* Endzustände */}
      <div className="text-[11px] font-semibold text-[#1A1A1A]/55 mb-1.5">Mögliche Ergebnisse (Status &amp; Tags)</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <OutcomeCard
          tone="green"
          badges={['Tag 189']}
          title="Leitcodierbar / Korrektur übernommen"
          sub="Tag DHL API geprüft, Auftrag bleibt im Versand-Status. Auch still-codeable-Fälle zählen hier."
        />
        <OutcomeCard
          tone="violet"
          badges={['Status 5.11']}
          title="Korrektur wartet auf Freigabe"
          sub="Halbautomatisch: Auftrag → 5.11, Vorschlag per Klick in der Korrektur-Freigabe übernehmen."
        />
        <OutcomeCard
          tone="red"
          badges={['Status 5.12', 'Tag 191']}
          title="Nicht korrigierbar"
          sub="Adressfehler: Status 5.12 + Tag 191 + deutsche Auftragsnotiz + E-Mail (einmalig beim Wechsel)."
        />
        <OutcomeCard
          tone="gray"
          badges={['—']}
          title="Übersprungen"
          sub="Kein DHL-Versand oder nicht-DE-Adresse → kein Tag, kein Fehler, keine Mail."
        />
      </div>

      <p className="text-[11px] text-[#1A1A1A]/55 mt-3">
        <strong>Der Modus steuert die Übernahme:</strong> Dry-Run schreibt nichts · Halbautomatisch → Freigabe-Liste · Vollautomatisch schreibt DHL-bestätigte Korrekturen direkt.
      </p>
    </div>
  );
}

function StatCard({ label, value, sub, tone, icon }: { label: string; value: string; sub?: string; tone?: 'green' | 'red'; icon?: React.ReactNode }) {
  const toneCls = tone === 'green' ? 'text-green-700' : tone === 'red' ? 'text-red-700' : 'text-[#1A1A1A]';
  return (
    <div className="rounded-xl border border-[#DAD9D9] p-3">
      <div className="text-xs text-[#1A1A1A]/55 flex items-center gap-1">{icon}{label}</div>
      <div className={`text-xl font-bold mt-0.5 ${toneCls}`}>{value}</div>
      {sub && <div className="text-[11px] text-[#1A1A1A]/50 mt-0.5">{sub}</div>}
    </div>
  );
}

function ToggleRow({
  icon,
  title,
  desc,
  on,
  onToggle,
  disabled,
  onColor = 'green',
  offColor = 'gray',
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  on: boolean;
  onToggle: () => void;
  disabled?: boolean;
  onColor?: 'green';
  offColor?: 'gray' | 'red';
}) {
  const trackOn = onColor === 'green' ? 'bg-green-500' : 'bg-green-500';
  const trackOff = offColor === 'red' ? 'bg-red-500' : 'bg-[#DAD9D9]';
  return (
    <div className="flex items-center justify-between rounded-xl border border-[#DAD9D9] p-3">
      <div className="text-sm pr-4">
        <div className="font-semibold text-[#1A1A1A] flex items-center gap-2">
          {icon} {title}
        </div>
        <p className="text-xs text-[#1A1A1A]/60 mt-0.5">{desc}</p>
      </div>
      <button
        onClick={onToggle}
        disabled={disabled}
        className={`relative w-14 h-7 shrink-0 rounded-full transition-colors disabled:opacity-50 ${on ? trackOn : trackOff}`}
        title={`${title} umschalten`}
      >
        <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full transition-all ${on ? 'left-7' : 'left-0.5'}`} />
      </button>
    </div>
  );
}

// ── DHL-Ausfall: Durchrutscher-Check (Finder, read-only) ───────────────────────
interface ReconcileRow {
  orderId: number;
  klass: 'resolved' | 'retriable' | 'recoverable' | 'shipped';
  statusId: number | null;
  lastCheckAt: string;
  lastError: string | null;
  address: string;
}
interface ReconcileData {
  generatedAt: string;
  sinceHours: number;
  candidates: number;
  checked: number;
  capped: boolean;
  counts: { recoverable: number; shipped: number; retriable: number; resolved: number; error: number };
  /** Status < 7 → werden automatisch nachgeprüft. */
  recoverable: ReconcileRow[];
  /** Status ≥ 7 (versandt) → zu spät. */
  shipped: ReconcileRow[];
}

function ReconcileTable({ rows }: { rows: ReconcileRow[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-[#DAD9D9]">
      <table className="w-full text-xs">
        <thead className="bg-[#DAD9D9]/30 text-left text-[#1A1A1A]/70">
          <tr>
            <th className="px-2 py-1.5 font-semibold">Auftrag</th>
            <th className="px-2 py-1.5 font-semibold">Adresse</th>
            <th className="px-2 py-1.5 font-semibold">Status jetzt</th>
            <th className="px-2 py-1.5 font-semibold">Letzter DHL-Fehler</th>
            <th className="px-2 py-1.5 font-semibold">Zuletzt geprüft</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.orderId} className="border-t border-[#DAD9D9]/60">
              <td className="px-2 py-1.5 font-mono">{r.orderId}</td>
              <td className="px-2 py-1.5">{r.address || '—'}</td>
              <td className="px-2 py-1.5">{r.statusId ?? '—'}</td>
              <td className="px-2 py-1.5 text-[#1A1A1A]/60">{r.lastError ?? '—'}</td>
              <td className="px-2 py-1.5 whitespace-nowrap">{new Date(r.lastCheckAt).toLocaleString('de-DE')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReconcileSection() {
  const [hours, setHours] = useState(72);
  const [data, setData] = useState<ReconcileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (h: number) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`${API_BASE}/reconcile/dhl-leaks`, { params: { sinceHours: h } });
      setData(r.data.data);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const RANGES: Array<[number, string]> = [
    [24, '24 h'],
    [72, '3 Tage'],
    [168, '7 Tage'],
  ];

  return (
    <Card title="DHL-Ausfall — Durchrutscher-Check" icon={<ShieldAlert className="w-5 h-5" />}>
      <p className="text-xs text-[#1A1A1A]/60 mb-3">
        Aufträge, deren letzte Prüfung an einem DHL-Fehler scheiterte und die den Trigger-Status
        (5.1&nbsp;/&nbsp;5.2&nbsp;/&nbsp;5.01) verlassen haben. <strong>Status&nbsp;&lt;&nbsp;7</strong> werden vom
        Polling automatisch erneut geprüft; <strong>Status&nbsp;≥&nbsp;7</strong> (versandt) sind zu spät und
        werden nur gemeldet. Diese Ansicht liest live gegen Plenty und schreibt nichts.
      </p>

      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {RANGES.map(([h, label]) => (
          <button
            key={h}
            onClick={() => setHours(h)}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
              hours === h ? 'bg-[#1A1A1A] text-white' : 'bg-[#DAD9D9]/40 text-[#1A1A1A]/70 hover:bg-[#DAD9D9]/70'
            }`}
          >
            {label}
          </button>
        ))}
        <button
          onClick={() => run(hours)}
          disabled={loading}
          className="ml-1 flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d] disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Prüfen
        </button>
      </div>

      {error && <div className="text-sm rounded-lg p-3 bg-red-50 text-red-700">{error}</div>}

      {data && (
        <div className="space-y-3">
          <div className="text-xs text-[#1A1A1A]/60">
            {data.candidates} Aufträge mit letzter Prüfung „DHL-Fehler" im Fenster · {data.checked} live geprüft
            {data.capped && ' (Limit erreicht — nicht alle geprüft)'}
            {data.counts.error > 0 && ` · ${data.counts.error} Live-Lesefehler`}
          </div>

          {data.recoverable.length === 0 && data.shipped.length === 0 ? (
            <div className="text-sm rounded-lg p-3 bg-green-50 text-green-800 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Nichts offen — alle DHL-Fehler-Aufträge sind wieder geprüft
              oder liegen noch im Trigger-Status (holt das Polling automatisch).
            </div>
          ) : (
            <>
              {data.recoverable.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-blue-800 mb-1.5 flex items-center gap-1.5">
                    <RefreshCw className="w-4 h-4" /> {data.recoverable.length} Aufträge (Status &lt; 7) — werden automatisch nachgeprüft
                  </div>
                  <ReconcileTable rows={data.recoverable} />
                  <p className="text-xs text-[#1A1A1A]/60 mt-1.5">
                    Das Polling (alle 5&nbsp;Min) prüft diese automatisch erneut, bis DHL antwortet oder der
                    Auftrag versandt ist. Kein manueller Eingriff nötig.
                  </p>
                </div>
              )}

              {data.shipped.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-amber-700 mb-1.5 flex items-center gap-1.5">
                    <ShieldAlert className="w-4 h-4" /> {data.shipped.length} Aufträge (Status ≥ 7, versandt) — zu spät
                  </div>
                  <ReconcileTable rows={data.shipped} />
                  <p className="text-xs text-[#1A1A1A]/60 mt-1.5">
                    Diese sind bereits im Warenausgang/versandt — eine Adressprüfung kommt zu spät. Nur zur
                    Kenntnis; ggf. Sendungsverfolgung im Blick behalten.
                  </p>
                </div>
              )}
            </>
          )}

          {data.counts.retriable > 0 && (
            <div className="text-xs rounded-lg p-2.5 bg-blue-50 text-blue-800">
              {data.counts.retriable} weitere Aufträge liegen noch im Trigger-Status — die holt der normale
              Polling-Sweep automatisch nach. Kein Handlungsbedarf.
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function Card({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-[#DAD9D9] p-4">
      <h3 className="text-base font-semibold text-[#1A1A1A] flex items-center gap-2 mb-2">
        <span className="text-[#1A1A1A]/70">{icon}</span> {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ l, v, on, ph }: { l: string; v: any; on: (v: string) => void; ph?: string }) {
  return (
    <div>
      <label className={label}>{l}</label>
      <input className={input} value={v ?? ''} placeholder={ph} onChange={(e) => on(e.target.value)} />
    </div>
  );
}
